import { gql } from '@sc/graphql';

export const GQL_QUERY_ME = gql(`
  query Me {
    user: me {
      ... UserFragment
    }
  }
`);

export const GQL_FRAGMENT_USER = gql(`
  fragment UserFragment on User {
    uuid
    email
    name
    verifiedEmailAt
    onboardingCompletedAt
    hasPendingEmail
    pendingEmail
    role
    company {
      uuid
      name
      billingCountry
      vatNumber
      billingAddressLine1
      billingAddressLine2
      billingCity
      billingPostalCode
      subscription {
        uuid
        subtotal
        type
        status
        tokenBillingType
        renewsAt
        expiresAt
      }
      limitations
      outstandingInvoice {
        uuid
        subscriptionUuid
      }
    }
  }
`);

export const GQL_MUTATION_REGISTER = gql(`
  mutation Register ($input: RegisterInput!) {
    register (input: $input) {
      accessToken
      companyUuid
    }
  }
`);

export const GQL_MUTATION_REQUEST_LOGIN_MAGIC_LINK = gql(`
  mutation RequestLoginMagicLink ($input: RequestLoginMagicLinkInput!) {
    requestLoginMagicLink (input: $input)
  }
`);

export const GQL_MUTATION_VERIFY_LOGIN_MAGIC_LINK = gql(`
  mutation VerifyLoginMagicLink ($input: VerifyLoginMagicLinkInput!) {
    verifyLoginMagicLink (input: $input) {
      accessToken
    }
  }
`);

export const GQL_MUTATION_AUTHORISE_WITH_AUTHORISATION_CDOE = gql(`
  mutation AuthoriseWithAuthorisationCode ($input: AuthorisationCodeInput!) {
    authoriseWithAuthorisationCode (input: $input) {
      accessToken
      registeredCompanyUuid
    }
  }
`);

export const GQL_QUERY_LOGOUT = gql(`
  query Logout {
    logout 
  }
`);

export const GQL_MUTATION_CONFIRM_EMAIL = gql(`
  mutation ConfirmEmail ($input: ConfirmEmailInput!) {
    confirmEmail (input: $input)
  }
`);

export const GQL_MUTATION_RESEND_VERIFICATION_EMAIL = gql(`
  mutation ResendVerificationEmail {
    resendVerificationEmail
  }
`);

export const GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID = gql(`
  query IndexSourceDocumentsForDocumentBrowser ($input: IndexSourceDocumentsInput!) {
    indexSourceDocuments (input: $input) {
      items {
        uuid
        hostedIntegrationDocumentId
        hostedIntegrationPath
      }
      pagination {
        lastPage
      }
    }
  }
`);

export const GQL_QUERY_INDEX_NOTIFICATIONS = gql(`
  query IndexNotifications {
    indexNotifications {
      uuid
      title
      description
      path
      isDismissed
      isViewed
      createdAt
    }
  }
`);

export const GQL_MUTATION_UPDATE_NOTIFICATION = gql(`
    mutation UpdateNotification ($input: UpdateNotificationInput!) {
      updateNotification (input: $input) {
        uuid
        title
        description
        path
        isDismissed
        isViewed
        createdAt
      }
    }
`);

export const GQL_QUERY_COUNT_QUERIES = gql(`
  query CountQueries ($dates: [DateTimeISO!]!, $unique: Boolean, $granularity: DateGranularity) {
    countQueries (dates: $dates, unique: $unique, granularity: $granularity) {
      date
      count
    }
  }
`);

export const GQL_QUERY_COUNT_USAGE_SUMMARY = gql(`
  query CountUsageSummary {
    countUsageSummary {
      users {
        current
        previous
        sparkline
      }
      queries {
        current
        previous
        sparkline
      }
      pages {
        current
        previous
        sparkline
      }
    }
  }
`);

export const GQL_QUERY_READ_PROJECT = gql(`
  query ReadProject ($uuid: String!) {
    readProject (uuid: $uuid) {
      ... Project 
    }
  }
`);

export const GQL_QUERY_READ_PROJECT_RULES = gql(`
  query ReadProjectRules ($uuid: String!) {
    readProject (uuid: $uuid) {
      uuid
      rules {
        uuid
        type
        value
      }
    }
  }
`);

export const GQL_FRAGMENT_PROJECT = gql(`
  fragment Project on Project {
    uuid
    name
    state
    sourceCount
    processedPc
    processedAt
    domainPrefix
    createdAt
    title
    subtitle
    questions {
      uuid
      question
    }
    domains {
      uuid
      url
    }
    theme {
      uuid
      name
      brandColour
      themeType
      title
      subtitle
      logoSrc
    }
  }
`);

export const GQL_QUERY_INDEX_PROJECTS = gql(`
  query IndexProjects ($input: IndexProjectsInput!) {
    indexProjects (input: $input) {
      pagination {
        lastPage
      }
      items {
        uuid
        name
        domains {
          uuid
          url 
        }
        theme {
          uuid
          name
        }
        state
        sourceCount
        dailyQueryCount
      }
    }
  }
`);

export const GQL_MUTATION_CREATE_PROJECT = gql(`
  mutation CreateProject ($input: CreateProjectInput!) {
    createProject (input: $input) {
      uuid
    }
  }
`);

export const GQL_MUTATION_CREATE_INTEGRATION = gql(`
  mutation CreateIntegration ($input: CreateIntegrationInput!) {
    createIntegration (input: $input)
  }
`);

export const GQL_QUERY_READ_INTEGRATION = gql(`
  query ReadIntegration ($type: IntegrationType!) {
    readIntegration (type: $type) {
      uuid
      state
    }
  }
`);

export const GQL_QUERY_READ_INTEGRATION_ACCESS_TOKEN = gql(`
  query ReadIntegrationAccessToken ($type: IntegrationType!) {
    readIntegrationAccessToken (type: $type)
  }
`);

export const GQL_QUERY_INDEX_INTEGRATIONS = gql(`
  query IndexIntegrations ($input: IndexIntegrationInput) {
    indexIntegrations (input: $input) {
      items {
        uuid
        type 
        state 
      }
    }
  }
`);

export const GQL_QUERY_READ_SOURCE = gql(`
  query ReadSource ($input: ReadSourceInput!) {
    readSource (input: $input) {
      uuid
      name
      type
    }
  }
`);

export const GQL_QUERY_INDEX_SOURCE_DOCUMENTS = gql(`
  query IndexSourceDocuments ($input: IndexSourceDocumentsInput!, $dimensionsInput: DimensionInput) {
    indexSourceDocuments (input: $input) {
      items {
        uuid 
        title 
        description 
        hostedIntegrationPath
        hostedIntegrationDocumentId
        processedAt
        failedReason
        url
        thumbnail {
          src (input: $dimensionsInput)
        }
      }
      pagination {
        lastPage
      }
    }
  }
`);

export const GQL_QUERY_INDEX_SOURCES = gql(`
  query IndexSources ($projectUuid: String!) {
    indexSources (projectUuid: $projectUuid) {
      uuid
      name
      documentCount
      type
    }
  }
`);

export const GQL_MUTATION_UPDATE_SOURCE = gql(`
  mutation UpdateSource ($input: UpdateSourceInput!) {
    updateSource (input: $input) {
      uuid
      name 
    }
  }
`);

export const GQL_QUERY_INDEX_INTEGRATION_DOCUMENTS = gql(`
  query IndexIntegrationDocuments ($input: IndexIntegrationDocumentsInput!) {
    indexIntegrationDocuments (input: $input) {
      items {
        id
        name
        hasChildren
        type
        relevance
        mimeType
        pathSegments {
          id
          name
          type
        }
      }
      pathSegments {
        id
        name
        type
      }
      nextPageToken
      totalPages
    }
  }
`);

export const GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS = gql(`
  mutation UpdateSourceDocuments ($input: UpdateSourceDocumentsInput!) {
    updateSourceDocuments (input: $input)
  }
`);

export const GQL_MUTATION_RESOLVE_INTEGRATION_SELECTION = gql(`
  mutation ResolveIntegrationSelection ($input: ResolveIntegrationSelectionInput!) {
    resolveIntegrationSelection (input: $input) {
      count
    }
  }
`);

export const GQL_MUTATION_GENERATE_PROJECT_QUESTIONS = gql(`
  mutation GenerateProjectQuestions ($projectUuid: String!) {
    generateProjectQuestions (projectUuid: $projectUuid) {
      ... Project
    }
  }
`);

export const GQL_MUTATION_UPDATE_PROJECT = gql(`
  mutation UpdateProject ($input: UpdateProjectInput!) {
    updateProject (input: $input) {
      ... Project
    }
  }
`);

export const GQL_QUERY_INDEX_DOMAINS = gql(`
  query IndexDomains ($input: IndexDomainsInput!) {
    indexDomains (input: $input) {
      items {
        uuid
        url
        projectUuid
        createdAt
        cloudflareStatus
      }
      pagination {
        lastPage
      }
    }
  }
`);

export const GQL_QUERY_READ_DOMAIN = gql(`
  query ReadDomain ($uuid: String!) {
    readDomain (uuid: $uuid) {
      uuid
      url
      cloudflareStatus
      verificationRecords {
        purpose
        name
        value
      }
    }
  }
`);

export const GQL_MUTATION_CREATE_DOMAIN = gql(`
  mutation CreateDomain ($input: CreateDomainInput!) {
    createDomain (input: $input) {
      uuid 
      url
      projectUuid 
      createdAt
    }
  }
`);

export const GQL_MUTATION_DELETE_DOMAIN = gql(`
  mutation DeleteDomain ($input: DeleteDomainInput!) {
    deleteDomain (input: $input)
  }
`);

export const GQL_MUTATION_SEND_DOMAIN_VERIFICATION_EMAIL = gql(`
  mutation SendDomainVerificationEmail ($input: SendDomainVerificationEmailInput!) {
    sendDomainVerificationEmail (input: $input)
  }
`);

export const GQL_FRAGMENT_THEME = gql(`
  fragment Theme on Theme {
    uuid
    name
    themeType
    brandColour
    title
    subtitle
    createdAt
    used
    projectCount
    logo {
      uuid
      name
      src (input: { width: 96 })
    }
  }
`);

export const GQL_QUERY_INDEX_THEMES = gql(`
  query IndexThemes ($input: IndexThemesInput!) {
    indexThemes (input: $input) {
      pagination {
        lastPage
      }
      items {
        ...Theme
      }
    }
  }
`);

export const GQL_QUERY_READ_THEME = gql(`
  query ReadTheme ($uuid: String!) {
    readTheme (uuid: $uuid) {
      ...Theme
    }
  }
`);

export const GQL_MUTATION_CREATE_THEME = gql(`
  mutation CreateTheme ($input: CreateThemeInput!) {
    createTheme (input: $input) {
      uuid
    }
  }
`);

export const GQL_MUTATION_UPDATE_THEME = gql(`
  mutation UpdateTheme ($input: UpdateThemeInput!) {
    updateTheme (input: $input) {
      ...Theme
    }
  }
`);

export const GQL_MUTATION_DELETE_THEME = gql(`
  mutation DeleteTheme ($uuid: String!) {
    deleteTheme (uuid: $uuid)
  }
`);

export const GQL_QUERY_READ_RATE_LIMIT = gql(`
  query ReadRateLimit ($projectUuid: String!) {
    readRateLimit (projectUuid: $projectUuid) {
      uuid
      queryMultiplier
      queriesPerUserPerHour
    }
  }
`);

export const GQL_MUTATION_UPDATE_RATE_LIMIT = gql(`
  mutation UpdateRateLimit ($input: UpdateRateLimit!) {
    updateRateLimit (input: $input) {
      uuid
      queryMultiplier
      queriesPerUserPerHour
    }
  }
`);

export const GQL_FRAGMENT_COMPANY_USER_ROW = gql(`
  fragment CompanyUserRow on CompanyUser {
    uuid
    userUuid
    companyUuid
    role
    createdAt
    deletedAt
    user {
      uuid
      name
      email
    }
    invitation {
      uuid
      expiresAt
    }
  }
`);

export const GQL_QUERY_INDEX_USERS = gql(`
  query IndexUsers ($input: IndexUsersInput!) {
    indexUsers (input: $input) {
      pagination {
        lastPage
      }
      items {
        ...CompanyUserRow
      }
    }
  }
`);

export const GQL_MUTATION_CREATE_INVITATION_FOR_USER = gql(`
  mutation CreateInvitationForUser ($input: CreateInvitationForUserInput!) {
    createInvitationForUser (input: $input) {
      ...CompanyUserRow
    }
  }
`);

export const GQL_MUTATION_UPDATE_COMPANY_USER = gql(`
  mutation UpdateCompanyUser ($input: UpdateUserInput!) {
    updateUser (input: $input) {
      ...CompanyUserRow
    }
  }
`);

export const GQL_MUTATION_DELETE_COMPANY_USER = gql(`
  mutation DeleteCompanyUser ($input: DeleteUserInput!) {
    deleteUser (input: $input)
  }
`);

export const GQL_MUTATION_RESEND_INVITE = gql(`
  mutation ResendInvite ($input: ResendInviteInput!) {
    resendInvite (input: $input) {
      ...CompanyUserRow
    }
  }
`);

export const GQL_MUTATION_ACCEPT_INVITATION = gql(`
  mutation AcceptInvitation ($input: AcceptInvitationInput!) {
    acceptInvitation (input: $input) {
      accessToken
    }
  }
`);

export const GQL_MUTATION_UPDATE_ME = gql(`
  mutation UpdateMe ($input: UpdateMeInput!) {
    updateMe (input: $input) {
      ...UserFragment
    }
  }
`);

export const GQL_MUTATION_DELETE_ME = gql(`
  mutation DeleteMe {
    deleteMe
  }
`);

export const GQL_MUTATION_UPDATE_COMPANY = gql(`
  mutation UpdateCompany ($input: UpdateCompanyInput!) {
    updateCompany (input: $input) {
      uuid
      name
    }
  }
`);

export const GQL_MUTATION_UPDATE_BILLING_DETAILS = gql(`
  mutation UpdateBillingDetails ($input: BillingDetailsInput!) {
    updateBillingDetails (input: $input) {
      company {
        uuid
        billingCountry
        vatNumber
        billingAddressLine1
        billingAddressLine2
        billingCity
        billingPostalCode
        subscription {
          uuid
          status
        }
      }
      removedPaymentMethods
    }
  }
`);

export const GQL_MUTATION_DELETE_COMPANY = gql(`
  mutation DeleteCompany {
    deleteCompany
  }
`);

export const GQL_QUERY_INDEX_USER_PREFERENCES = gql(`
  query IndexUserPreferences ($input: IndexUserPreferencesInput!) {
    indexUserPreferences (input: $input) {
      userUuid
      preferenceUuid
      value
      preference {
        uuid
        group
        name
        displayName
      }
    }
  }
`);

export const GQL_MUTATION_UPDATE_USER_PREFERENCE = gql(`
  mutation UpdateUserPreference ($input: UpdateUserPreferenceInput!) {
    updateUserPreference (input: $input) {
      userUuid
      preferenceUuid
      value
      preference {
        uuid
        group
        name
        displayName
      }
    }
  }
`);

export const GQL_QUERY_NOTIFICATION_PREFERENCES = gql(`
  query NotificationPreferences {
    notificationPreferences {
      uuid
      notificationType
      notificationValue
      isWeb
      isEmail
    }
  }
`);

export const GQL_MUTATION_CREATE_NOTIFICATION_PREFERENCE = gql(`
  mutation CreateNotificationPreference ($input: CreateNotificationPreferenceInput!) {
    createNotificationPreference (input: $input) {
      uuid
      notificationType
      notificationValue
      isWeb
      isEmail
    }
  }
`);

export const GQL_MUTATION_UPDATE_NOTIFICATION_PREFERENCE = gql(`
  mutation UpdateNotificationPreference ($input: UpdateNotificationPreferenceInput!) {
    updateNotificationPreference (input: $input) {
      uuid
      notificationType
      notificationValue
      isWeb
      isEmail
    }
  }
`);

export const GQL_MUTATION_DELETE_NOTIFICATION_PREFERENCE = gql(`
  mutation DeleteNotificationPreference ($input: DeleteNotificationPreferenceInput!) {
    deleteNotificationPreference (input: $input)
  }
`);

export const GQL_MUTATION_CREATE_SUBSCRIPTION_INTENT = gql(`
  mutation CreateSubscriptionIntent ($input: CreateSubscriptionIntentInput!) {
    createSubscriptionIntent (input: $input) {
      succeeded
      clientSecret
    }
  }
`);

export const GQL_MUTATION_PAY_INVOICE = gql(`
  mutation PayInvoice ($input: PayInvoiceInput!) {
    payInvoice (input: $input) {
      succeeded
      clientSecret
    }
  }
`);

export const GQL_QUERY_PREVIEW_PLAN_CHANGE = gql(`
  query PreviewPlanChange ($priceStep: Int!) {
    previewPlanChange (priceStep: $priceStep) {
      discount
      charge
    }
  }
`);

export const GQL_QUERY_READ_USAGE = gql(`
  query ReadUsage {
    readUsage {
      queries { total used }
      pages { total used }
    }
  }
`);

export const GQL_QUERY_LIST_PAYMENT_METHODS = gql(`
  query ListPaymentMethods {
    listPaymentMethods {
      uuid
      brand
      last4
      expMonth
      expYear
      useForMonthlyPlan
    }
  }
`);

export const GQL_MUTATION_CREATE_SETUP_INTENT = gql(`
  mutation CreateSetupIntent {
    createSetupIntent {
      clientSecret
    }
  }
`);

export const GQL_MUTATION_REMOVE_PAYMENT_METHOD = gql(`
  mutation RemovePaymentMethod ($input: RemovePaymentMethodInput!) {
    removePaymentMethod (input: $input)
  }
`);

export const GQL_MUTATION_SAVE_PAYMENT_METHOD = gql(`
  mutation SavePaymentMethod ($input: SavePaymentMethodInput!) {
    savePaymentMethod (input: $input) {
      uuid
    }
  }
`);

export const GQL_MUTATION_UPDATE_SUBSCRIPTION = gql(`
  mutation UpdateSubscription ($action: SubscriptionAction!) {
    updateSubscription (action: $action)
  }
`);

export const GQL_QUERY_LIST_INVOICES = gql(`
  query ListInvoices ($input: ListInvoicesInput!) {
    listInvoices (input: $input) {
      pagination {
        lastPage
      }
      items {
        uuid
        description
        subscriptionType
        currency
        subtotal
        tax
        total
        status
        paidAt
        createdAt
      }
    }
  }
`);

export const GQL_QUERY_READ_INVOICE = gql(`
  query ReadInvoice ($uuid: String!) {
    readInvoice (uuid: $uuid) {
      uuid
      description
      currency
      subtotal
      tax
      total
      status
      paidAt
      createdAt
      stripeDetails {
        number
        hostedInvoiceUrl
        invoicePdfUrl
        dueDate
        paymentMethodBrand
        paymentMethodLast4
      }
    }
  }
`);
